import { useState, useEffect } from "react";
import {
  Brain, RefreshCw, CheckCircle, XCircle, AlertTriangle, Search,
  Shield, Ban, Lightbulb, DollarSign, Clock, ChevronDown, ChevronUp,
  Settings, Plus, X, Save, History, ShieldCheck, TrendingDown, Eye,
  Sparkles, Zap, BookOpen, Wifi, WifiOff, Link2
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

// Safe JSON parser — prevents "body stream already read" errors
const safeJson = async (res) => {
  try {
    return await res.json();
  } catch (e) {
    if (e.message?.includes("body stream already read") || e.message?.includes("locked")) {
      try { return await res.clone().json(); } catch { return { error: "json_parse_error", detail: e.message }; }
    }
    return { error: "json_parse_error", detail: e.message };
  }
};

const VERDICT_CONFIG = {
  pertinent: { label: "Pertinent", icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/10 border-green-500/25" },
  douteux: { label: "Douteux", icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/25" },
  hors_sujet: { label: "Hors sujet", icon: XCircle, color: "text-red-400", bg: "bg-red-500/10 border-red-500/25" },
  hors_zone: { label: "Hors zone", icon: Ban, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/25" },
};

const PERIODS = [
  { value: 1, label: "24h" },
  { value: 7, label: "7j" },
  { value: 14, label: "14j" },
  { value: 30, label: "30j" },
  { value: 90, label: "90j" },
];

export default function GoogleAdsSettings() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showRecos, setShowRecos] = useState(true);
  const [days, setDays] = useState(30);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [campaignList, setCampaignList] = useState([]);

  // Config
  const [config, setConfig] = useState({ services: [], business_context: "", configured: false });
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [newService, setNewService] = useState("");
  const [showConfig, setShowConfig] = useState(false);

  // Negatives
  const [existingNegatives, setExistingNegatives] = useState([]);
  const [showNegatives, setShowNegatives] = useState(false);
  const [selectedForExclusion, setSelectedForExclusion] = useState(new Set());
  const [excluding, setExcluding] = useState(false);

  // History
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Whitelist
  const [whitelist, setWhitelist] = useState([]);
  const [showWhitelist, setShowWhitelist] = useState(false);

  // Waste
  const [waste, setWaste] = useState(null);

  // Savings
  const [savings, setSavings] = useState(null);

  // David Intelligence
  const [davidIntel, setDavidIntel] = useState(null);
  const [showDavidIntel, setShowDavidIntel] = useState(false);
  const [syncingMemory, setSyncingMemory] = useState(false);

  // David Recommendations
  const [recommendations, setRecommendations] = useState(null);
  const [loadingRecos, setLoadingRecos] = useState(false);
  const [selectedRecos, setSelectedRecos] = useState(new Set());

  // Shared lists + exclusion modal
  const [sharedLists, setSharedLists] = useState([]);
  const [showExclusionModal, setShowExclusionModal] = useState(false);
  const [exclusionTarget, setExclusionTarget] = useState("shared_list"); // "campaign" | "shared_list" | "new_list" | "account"
  const [exclusionCampaignId, setExclusionCampaignId] = useState("");
  const [exclusionListId, setExclusionListId] = useState("");
  const [newListName, setNewListName] = useState("");
  const [accountLevelLists, setAccountLevelLists] = useState([]);
  const [selectedAccountListId, setSelectedAccountListId] = useState("");

  // Connection status
  const [adsStatus, setAdsStatus] = useState({ loading: true, connected: false });
  const [campaignError, setCampaignError] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    checkAdsStatus();
    loadCachedAnalysis();
    loadConfig();
    loadCampaignList();
    loadExistingNegatives();
    loadHistory();
    loadWhitelist();
    loadWaste();
    loadSharedLists();
    loadAccountLevelLists();
    loadSavings();
    loadDavidIntel();
    // Listen for OAuth popup
    const handleMsg = (e) => {
      if (e.data?.type === 'google-ads-auth') {
        setReconnecting(false);
        if (e.data.success) {
          checkAdsStatus();
          loadCampaignList();
        } else {
          setCampaignError(e.data.message || "Erreur d'authentification");
        }
      }
    };
    window.addEventListener('message', handleMsg);
    return () => window.removeEventListener('message', handleMsg);
  }, []);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await authFetch(`${API}/google-ads/config`);
      const data = await safeJson(res);
      setConfig(data);
      if (!data.configured) setShowConfig(true);
    } catch {}
    setConfigLoading(false);
  };

  const checkAdsStatus = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/auth/status`);
      const data = await safeJson(res);
      setAdsStatus({ loading: false, ...data });
    } catch {
      setAdsStatus({ loading: false, connected: false, message: "Erreur de vérification" });
    }
  };

  const reconnectAds = async () => {
    setReconnecting(true);
    setCampaignError(null);
    try {
      const res = await authFetch(`${API}/google-ads/auth/url`);
      const { auth_url } = await safeJson(res);
      if (auth_url) {
        const popup = window.open(auth_url, 'google-ads-auth', 'width=600,height=700');
        if (!popup) {
          setCampaignError("Popup bloqué par le navigateur");
          setReconnecting(false);
          return;
        }
        const chk = setInterval(() => {
          if (popup?.closed) {
            clearInterval(chk);
            setReconnecting(false);
            setTimeout(() => { checkAdsStatus(); loadCampaignList(); }, 1000);
          }
        }, 1000);
      }
    } catch {
      setCampaignError("Erreur lors de la reconnexion");
      setReconnecting(false);
    }
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      await authFetch(`${API}/google-ads/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ services: config.services, business_context: config.business_context }),
      });
      setConfig({ ...config, configured: true });
    } catch {}
    setConfigSaving(false);
  };

  const addService = () => {
    const s = newService.trim();
    if (s && !config.services.includes(s)) {
      setConfig({ ...config, services: [...config.services, s] });
      setNewService("");
    }
  };

  const removeService = (s) => setConfig({ ...config, services: config.services.filter(x => x !== s) });

  const loadCachedAnalysis = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/google-ads/david-analysis-cache`);
      const data = await safeJson(res);
      if (data.cached) setAnalysis(data);
    } catch {}
    setLoading(false);
  };

  const loadCampaignList = async () => {
    setCampaignError(null);
    try {
      const res = await authFetch(`${API}/google-ads/campaign-list`);
      const data = await safeJson(res);
      if (data.error) {
        if (data.error === "no_token") {
          setCampaignError("Token Google Ads expiré — reconnexion nécessaire");
        } else {
          setCampaignError(`Erreur API: ${data.error}${data.detail ? ` — ${data.detail.substring(0, 100)}` : ""}`);
        }
        setCampaignList([]);
      } else {
        setCampaignList(data.campaigns || []);
        if ((data.campaigns || []).length === 0) {
          setCampaignError("Aucune campagne retournée par Google Ads");
        }
      }
    } catch (e) {
      setCampaignError("Impossible de charger les campagnes");
      setCampaignList([]);
    }
  };

  const loadExistingNegatives = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/negative-keywords`);
      const data = await safeJson(res);
      setExistingNegatives(data.negatives || []);
    } catch {}
  };

  const loadHistory = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/david-history`);
      const data = await safeJson(res);
      setHistory(data.history || []);
    } catch {}
  };

  const loadWhitelist = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/whitelist`);
      const data = await safeJson(res);
      setWhitelist(data.whitelist || []);
    } catch {}
  };

  const loadWaste = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/waste-summary`);
      const data = await safeJson(res);
      setWaste(data);
    } catch {}
  };

  const loadSharedLists = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/shared-lists`);
      const data = await safeJson(res);
      setSharedLists(data.lists || []);
      if (data.lists?.length > 0 && !exclusionListId) {
        setExclusionListId(String(data.lists[0].id));
      }
    } catch {}
  };

  const loadAccountLevelLists = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/account-level-lists`);
      const data = await safeJson(res);
      setAccountLevelLists(data.lists || []);
    } catch {}
  };

  const loadSavings = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/savings`);
      const data = await safeJson(res);
      setSavings(data);
    } catch {}
  };

  const loadDavidIntel = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/david-intelligence`);
      const data = await safeJson(res);
      setDavidIntel(data);
    } catch {}
  };

  const syncDavidMemory = async () => {
    setSyncingMemory(true);
    try {
      const res = await authFetch(`${API}/clones/persons/david/sources/google_ads_exclusions/sync`, { method: "POST" });
      const data = await safeJson(res);
      if (data.result) {
        await loadDavidIntel();
      }
    } catch {}
    setSyncingMemory(false);
  };

  const runRecommendations = async () => {
    setLoadingRecos(true);
    setRecommendations(null);
    setSelectedRecos(new Set());
    try {
      const res = await authFetch(`${API}/google-ads/david-recommendations?days=${days}`, { method: "POST" });
      const data = await safeJson(res);
      setRecommendations(data);
    } catch (err) {
      console.error("runRecommendations error:", err);
    }
    setLoadingRecos(false);
  };

  const toggleReco = (term) => {
    const next = new Set(selectedRecos);
    next.has(term) ? next.delete(term) : next.add(term);
    setSelectedRecos(next);
  };

  const selectAllRecos = () => {
    if (!recommendations?.recommendations) return;
    setSelectedRecos(new Set(recommendations.recommendations.map(r => r.term)));
  };

  const excludeRecos = () => {
    if (selectedRecos.size === 0) return;
    setSelectedForExclusion(selectedRecos);
    setShowExclusionModal(true);
  };

  const runAnalysis = async () => {
    if (!config.configured && config.services.length === 0) {
      alert("Configurez d'abord vos services.");
      setShowConfig(true);
      return;
    }
    setAnalyzing(true);
    try {
      const params = `days=${days}${campaignFilter ? `&campaign_name=${encodeURIComponent(campaignFilter)}` : ""}`;
      const res = await authFetch(`${API}/google-ads/david-analyze?${params}`, { method: "POST" });
      const data = await safeJson(res);
      if (data.terms) {
        setAnalysis({ ...data, cached: true });
        setSelectedForExclusion(new Set());
        loadHistory();
        loadWaste();
      } else {
        alert("Erreur: " + (data.detail || "Analyse echouee"));
      }
    } catch (err) {
      console.error("runAnalysis error:", err);
      alert("Erreur d'analyse: " + err.message);
    }
    setAnalyzing(false);
  };

  const toggleExclusion = (term) => {
    const next = new Set(selectedForExclusion);
    next.has(term) ? next.delete(term) : next.add(term);
    setSelectedForExclusion(next);
  };

  const selectAllNegatives = () => {
    const terms = (analysis?.terms || [])
      .filter(t => t.verdict === "hors_sujet" || t.verdict === "hors_zone")
      .map(t => t.term);
    setSelectedForExclusion(new Set(terms));
  };

  const excludeSelected = async () => {
    if (selectedForExclusion.size === 0) return;
    setShowExclusionModal(true);
  };

  const confirmExclusion = async () => {
    if (selectedForExclusion.size === 0) return;
    setExcluding(true);
    try {
      let body = { keywords: [...selectedForExclusion] };

      if (exclusionTarget === "shared_list" && exclusionListId) {
        body.target = "shared_list";
        body.list_id = exclusionListId;
      } else if (exclusionTarget === "campaign" && exclusionCampaignId) {
        body.target = "campaign";
        body.campaign_id = exclusionCampaignId;
      } else if (exclusionTarget === "new_list" && newListName.trim()) {
        const createRes = await authFetch(`${API}/google-ads/shared-lists`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: newListName.trim() }),
        });
        let createData;
        createData = await safeJson(createRes);
        if (!createData.success) {
          alert("Erreur creation liste: " + (createData.detail || "Echec"));
          setExcluding(false);
          return;
        }
        body.target = "shared_list";
        body.list_id = createData.id;
      } else if (exclusionTarget === "account") {
        body.target = "account";
        if (selectedAccountListId) {
          body.account_list_id = selectedAccountListId;
        }
      } else {
        alert("Selectionnez une destination");
        setExcluding(false);
        return;
      }

      const res = await authFetch(`${API}/google-ads/negative-keywords`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      let data;
      data = await safeJson(res);

      if (data.success) {
        const targetLabel = body.target === "campaign" ? "la campagne" : body.target === "account" ? "le compte (toutes campagnes)" : "la liste";
        let msg = `${data.added} mots-cles negatifs ajoutes dans ${targetLabel} !`;
        if (data.skipped > 0) msg += `\n${data.skipped} termes ignores (trop longs pour Google Ads).`;
        if (data.warning) msg += `\n\nAttention: ${data.warning}`;
        alert(msg);
        setSelectedForExclusion(new Set());
        setShowExclusionModal(false);
        setNewListName("");
        loadExistingNegatives();
        loadSharedLists();
        loadAccountLevelLists();
        loadSavings();
      } else {
        alert("Erreur: " + (data.detail || "Echec"));
      }
    } catch (err) {
      console.error("confirmExclusion error:", err);
      alert("Erreur d'exclusion: " + err.message);
    }
    setExcluding(false);
  };

  const addToWhitelist = async (term) => {
    try {
      const res = await authFetch(`${API}/google-ads/whitelist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ terms: [term] }),
      });
      const data = await safeJson(res);
      if (data.success) {
        loadWhitelist();
      }
    } catch {}
  };

  const removeFromWhitelist = async (term) => {
    try {
      await authFetch(`${API}/google-ads/whitelist`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ term }),
      });
      loadWhitelist();
    } catch {}
  };

  const fmt = v => (v ?? 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
  const whitelistedSet = new Set(whitelist.map(w => w.term));

  if (loading || configLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Exclusion Modal */}
      {showExclusionModal && (
        <ExclusionModal
          count={selectedForExclusion.size}
          target={exclusionTarget} setTarget={setExclusionTarget}
          campaignList={campaignList} campaignId={exclusionCampaignId} setCampaignId={setExclusionCampaignId}
          sharedLists={sharedLists} listId={exclusionListId} setListId={setExclusionListId}
          newListName={newListName} setNewListName={setNewListName}
          onConfirm={confirmExclusion} onCancel={() => setShowExclusionModal(false)}
          excluding={excluding}
          accountLevelLists={accountLevelLists}
          selectedAccountListId={selectedAccountListId}
          setSelectedAccountListId={setSelectedAccountListId}
        />
      )}

      {/* Connection Status Banner */}
      {(!adsStatus.loading && (!adsStatus.connected || campaignError)) && (
        <div className={`rounded-xl border p-4 flex items-center justify-between flex-wrap gap-3 ${
          adsStatus.connected ? "bg-amber-500/5 border-amber-500/30" : "bg-red-500/5 border-red-500/30"
        }`} data-testid="ads-connection-banner">
          <div className="flex items-center gap-3">
            {adsStatus.connected ? (
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-400 flex-shrink-0" />
            )}
            <div>
              <p className={`text-sm font-medium ${adsStatus.connected ? "text-amber-300" : "text-red-300"}`}>
                {!adsStatus.connected ? "Google Ads déconnecté" : "Problème de connexion campagnes"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {campaignError || adsStatus.message || "Le token a expiré. Reconnectez-vous pour accéder aux campagnes."}
              </p>
            </div>
          </div>
          <button
            onClick={reconnectAds}
            disabled={reconnecting}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center gap-2 transition-all"
            data-testid="reconnect-ads-btn"
          >
            {reconnecting ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Connexion...</>
            ) : (
              <><Link2 className="w-4 h-4" /> Reconnecter Google Ads</>
            )}
          </button>
        </div>
      )}

      {/* Waste Counter + Savings KPI */}
      {(waste?.current_waste > 0 || savings?.total_savings > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {waste && waste.current_waste > 0 && <WasteCounter waste={waste} fmt={fmt} />}
          {savings && savings.total_savings > 0 && <SavingsCounter savings={savings} fmt={fmt} />}
        </div>
      )}

      {/* David Intelligence */}
      {davidIntel && davidIntel.total_rules > 0 && (
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-amber-500/20 rounded-xl overflow-hidden" data-testid="david-intelligence-panel">
          <button onClick={() => setShowDavidIntel(!showDavidIntel)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/80 transition-colors" data-testid="toggle-david-intel">
            <span className="font-semibold text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300">David Intelligence</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">{davidIntel.total_rules} regles apprises</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{davidIntel.total_keywords} mots-cles memorises</span>
            </span>
            {showDavidIntel ? <ChevronUp className="w-4 h-4 text-amber-400" /> : <ChevronDown className="w-4 h-4 text-amber-400" />}
          </button>
          {showDavidIntel && (
            <div className="px-4 pb-4 border-t border-amber-500/10 pt-3 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-400">
                  {davidIntel.last_sync ? `Derniere synchro: ${new Date(davidIntel.last_sync).toLocaleString("fr-FR")}` : "Jamais synchronise"}
                </p>
                <button onClick={syncDavidMemory} disabled={syncingMemory}
                  className="px-3 py-1 bg-amber-600/20 border border-amber-500/30 rounded-md text-xs text-amber-300 hover:bg-amber-600/40 disabled:opacity-50 flex items-center gap-1.5 transition-colors" data-testid="sync-david-memory-btn">
                  <RefreshCw className={`w-3 h-3 ${syncingMemory ? "animate-spin" : ""}`} />
                  {syncingMemory ? "Sync..." : "Re-synchroniser"}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {davidIntel.rules.map((rule, i) => (
                  <div key={i} className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-3 hover:border-amber-500/30 transition-colors">
                    <div className="flex items-start gap-2">
                      <Zap className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-white">{rule.title}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{rule.description}</p>
                        {rule.examples.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {rule.examples.slice(0, 3).map((ex, j) => (
                              <span key={j} className="px-1.5 py-0.5 bg-slate-700/50 rounded text-[10px] text-slate-400 font-mono">{ex}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 text-center mt-2 flex items-center justify-center gap-1">
                <BookOpen className="w-3 h-3" />
                Ces regles sont injectees automatiquement dans chaque analyse David
              </p>
            </div>
          )}
        </div>
      )}

      {/* David Recommendations Results */}
      {recommendations && recommendations.recommendations && (
        <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/30 rounded-xl p-4 space-y-3" data-testid="david-recommendations-panel">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-purple-300 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Preconisations David
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
                {recommendations.total_recommendations} termes a exclure
              </span>
            </h3>
            <div className="flex items-center gap-2">
              {recommendations.estimated_waste > 0 && (
                <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-300 font-medium" data-testid="recos-waste">
                  Gaspillage estime: {recommendations.estimated_waste.toFixed(2)}€
                </span>
              )}
              <span className="text-[10px] text-slate-500">{recommendations.total_scanned} termes scannes</span>
            </div>
          </div>

          {recommendations.recommendations.length > 0 ? (
            <>
              <div className="flex items-center gap-2">
                <button onClick={selectAllRecos}
                  className="px-3 py-1 bg-purple-600/20 border border-purple-500/30 rounded-md text-xs text-purple-300 hover:bg-purple-600/40 transition-colors" data-testid="select-all-recos">
                  Tout selectionner ({recommendations.recommendations.length})
                </button>
                {selectedRecos.size > 0 && (
                  <button onClick={excludeRecos}
                    className="px-3 py-1 bg-red-600/30 border border-red-500/30 rounded-md text-xs text-red-300 hover:bg-red-600/50 transition-colors flex items-center gap-1" data-testid="exclude-recos-btn">
                    <Ban className="w-3 h-3" />
                    Exclure {selectedRecos.size} termes
                  </button>
                )}
              </div>
              <div className="max-h-[400px] overflow-y-auto space-y-1.5">
                {recommendations.recommendations.map((rec, i) => (
                  <div key={i}
                    onClick={() => toggleReco(rec.term)}
                    className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                      selectedRecos.has(rec.term) ? "bg-purple-500/20 border border-purple-500/40" : "bg-slate-800/50 border border-slate-700/30 hover:border-purple-500/30"
                    }`}
                    data-testid={`reco-item-${i}`}>
                    <input type="checkbox" checked={selectedRecos.has(rec.term)} readOnly
                      className="mt-1 accent-purple-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-white">{rec.term}</span>
                        {rec.cost > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">{rec.cost.toFixed(2)}€</span>}
                        {rec.clicks > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{rec.clicks} clics</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5" />{rec.rule_matched}
                        </span>
                        <span className="text-[10px] text-slate-500">{rec.reason}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                      rec.confidence >= 90 ? "bg-red-500/20 text-red-300" : rec.confidence >= 70 ? "bg-amber-500/20 text-amber-300" : "bg-slate-700/50 text-slate-400"
                    }`}>{rec.confidence}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-400 text-center py-4">Aucune preconisation — tous les termes semblent pertinents selon les regles de David.</p>
          )}
        </div>
      )}

      {/* Config Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <button onClick={() => setShowConfig(!showConfig)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/80 transition-colors" data-testid="toggle-config">
          <span className="font-semibold text-sm flex items-center gap-2">
            <Settings className="w-4 h-4 text-slate-400" />
            Contexte metier
            {config.configured ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Configure</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">A configurer</span>}
          </span>
          {showConfig ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showConfig && (
          <div className="px-4 pb-4 space-y-4 border-t border-slate-700/30 pt-3">
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-2">Services proposes</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {config.services.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500/15 border border-blue-500/25 rounded-md text-xs text-blue-300">
                    {s}
                    <button onClick={() => removeService(s)} className="hover:text-red-400 ml-0.5"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {config.services.length === 0 && <span className="text-xs text-slate-500 italic">Aucun service</span>}
              </div>
              <div className="flex items-center gap-2">
                <input type="text" value={newService} onChange={e => setNewService(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addService(); }}
                  placeholder="Ex: Douche seniors, Volets roulants..." className="flex-1 bg-slate-900/80 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-white outline-none focus:border-blue-500/50" data-testid="new-service-input" />
                <button onClick={addService} className="px-3 py-1.5 bg-blue-600/30 border border-blue-500/30 rounded-md text-sm text-blue-300 hover:bg-blue-600/50 transition-colors flex items-center gap-1" data-testid="add-service-btn">
                  <Plus className="w-3.5 h-3.5" /> Ajouter
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-2">Contexte supplementaire</label>
              <textarea value={config.business_context} onChange={e => setConfig({ ...config, business_context: e.target.value })}
                placeholder="Ex: On ne fait pas la peinture. Monte-escalier = pas prioritaire..."
                className="w-full bg-slate-900/80 border border-slate-700 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-blue-500/50 min-h-[80px] resize-y" data-testid="business-context-input" />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-slate-500">Zones d'intervention : depuis Config Pipeline</p>
              <button onClick={saveConfig} disabled={configSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors" data-testid="save-config-btn">
                <Save className="w-4 h-4" />{configSaving ? "..." : "Enregistrer"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Analysis toolbar */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5 text-amber-400" />
            <span className="font-semibold text-sm">Analyse David</span>

            <div className="flex items-center bg-slate-900/80 rounded-lg p-0.5" data-testid="analysis-period-selector">
              {PERIODS.map(p => (
                <button key={p.value} onClick={() => setDays(p.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${days === p.value ? "bg-amber-600 text-white shadow" : "text-slate-400 hover:text-white"}`}
                  data-testid={`analysis-period-${p.value}`}>{p.label}</button>
              ))}
            </div>

            <select value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}
              className={`bg-slate-900/80 border rounded-md px-2 py-1 text-xs text-white outline-none ${campaignList.length === 0 && campaignError ? "border-red-500/50" : "border-slate-700"}`} data-testid="campaign-filter-select"
              title={campaignList.length === 0 && campaignError ? campaignError : ""}>
              <option value="">{campaignList.length === 0 && campaignError ? "⚠ Campagnes indisponibles" : "Toutes campagnes"}</option>
              {campaignList.filter(c => c.status === "ENABLED").map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
              <optgroup label="En pause">
                {campaignList.filter(c => c.status !== "ENABLED").map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <button onClick={runAnalysis} disabled={analyzing}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 text-white font-semibold rounded-lg flex items-center gap-2 transition-all text-sm"
            data-testid="launch-analysis-btn">
            {analyzing ? <><RefreshCw className="w-4 h-4 animate-spin" />Analyse en cours (tous les termes)...</> : <><Brain className="w-4 h-4" />Lancer l'analyse</>}
          </button>

          {davidIntel?.total_rules > 0 && (
            <button onClick={runRecommendations} disabled={loadingRecos}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 text-white font-semibold rounded-lg flex items-center gap-2 transition-all text-sm"
              data-testid="launch-recommendations-btn">
              {loadingRecos ? <><RefreshCw className="w-4 h-4 animate-spin" />Scan en cours...</> : <><Sparkles className="w-4 h-4" />Preconisations David</>}
            </button>
          )}
        </div>
      </div>

      {/* No analysis yet */}
      {!analysis && !analyzing && (
        <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/30 rounded-xl p-8 text-center">
          <Brain className="w-12 h-12 text-amber-400 mx-auto mb-3 opacity-70" />
          <p className="text-slate-400 text-sm">Selectionnez une periode et cliquez "Lancer l'analyse"</p>
        </div>
      )}

      {/* Analysis results */}
      {analysis && (
        <AnalysisResults
          analysis={analysis} filter={filter} setFilter={setFilter}
          showRecos={showRecos} setShowRecos={setShowRecos}
          selectedForExclusion={selectedForExclusion} toggleExclusion={toggleExclusion}
          selectAllNegatives={selectAllNegatives} excludeSelected={excludeSelected} excluding={excluding}
          fmt={fmt} addToWhitelist={addToWhitelist} whitelistedSet={whitelistedSet}
        />
      )}

      {/* History */}
      <HistorySection history={history} showHistory={showHistory} setShowHistory={setShowHistory} fmt={fmt} />

      {/* Whitelist */}
      <WhitelistSection whitelist={whitelist} showWhitelist={showWhitelist} setShowWhitelist={setShowWhitelist} removeFromWhitelist={removeFromWhitelist} />

      {/* Shared lists with campaign links */}
      <SharedListsSection sharedLists={sharedLists} campaignList={campaignList} loadSharedLists={loadSharedLists} />

      {/* Existing account-level negatives */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <button onClick={() => { setShowNegatives(!showNegatives); if (!showNegatives) loadExistingNegatives(); }}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/80 transition-colors" data-testid="toggle-negatives">
          <span className="font-semibold text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-400" />
            Mots-cles negatifs compte ({existingNegatives.length})
          </span>
          {showNegatives ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showNegatives && (
          <div className="px-4 pb-4 border-t border-slate-700/30 pt-3">
            {existingNegatives.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Aucun mot-cle negatif au niveau du compte</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {existingNegatives.map((n, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-700/50 border border-slate-600/30 rounded text-xs text-slate-300 font-mono">
                    -{n.text} <span className="text-slate-500 text-[10px]">({n.match_type})</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════ WASTE COUNTER KPI ═══════ */
function WasteCounter({ waste, fmt }) {
  const trend = waste.trend || [];
  const maxWaste = Math.max(...trend.map(t => t.waste_computed || 0), 1);
  const improving = trend.length >= 2 && (trend[trend.length - 1]?.waste_computed || 0) < (trend[trend.length - 2]?.waste_computed || 0);

  return (
    <div className="bg-gradient-to-r from-red-900/30 via-red-900/20 to-slate-800/50 border border-red-500/30 rounded-xl p-4" data-testid="waste-counter">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Budget gaspille (hors sujet + hors zone)</p>
            <p className="text-2xl font-bold text-red-400" data-testid="waste-amount">{fmt(waste.current_waste)}</p>
            <p className="text-[11px] text-slate-500">
              sur {waste.period_days}j — {(waste.verdict_counts?.hors_sujet || 0) + (waste.verdict_counts?.hors_zone || 0)} termes problematiques
            </p>
          </div>
        </div>

        {/* Mini sparkline */}
        {trend.length > 1 && (
          <div className="flex items-end gap-0.5 h-10" data-testid="waste-trend">
            {trend.map((t, i) => {
              const h = Math.max(4, ((t.waste_computed || 0) / maxWaste) * 40);
              const isLast = i === trend.length - 1;
              return (
                <div key={i} className={`w-2.5 rounded-t transition-all ${isLast ? "bg-red-400" : "bg-red-400/30"}`}
                  style={{ height: `${h}px` }}
                  title={`${fmt(t.waste_computed || 0)}`} />
              );
            })}
            {improving && (
              <span className="ml-2 text-[10px] text-green-400 flex items-center gap-0.5">
                <TrendingDown className="w-3 h-3" /> En baisse
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════ SAVINGS COUNTER KPI ═══════ */
function SavingsCounter({ savings, fmt }) {
  return (
    <div className="bg-gradient-to-r from-emerald-900/30 via-emerald-900/20 to-slate-800/50 border border-emerald-500/30 rounded-xl p-4" data-testid="savings-counter">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <TrendingDown className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Economies realisees (termes exclus)</p>
            <p className="text-2xl font-bold text-emerald-400" data-testid="savings-amount">{fmt(savings.total_savings)}</p>
            <p className="text-[11px] text-slate-500">
              {savings.total_excluded} termes exclus en {savings.actions_count} action{savings.actions_count > 1 ? "s" : ""}
              {savings.monthly_projection > 0 && <> — projection mensuelle : <span className="text-emerald-400 font-medium">{fmt(savings.monthly_projection)}/mois</span></>}
            </p>
          </div>
        </div>

        {/* Actions breakdown */}
        {savings.actions?.length > 0 && (
          <div className="hidden md:flex flex-col items-end gap-0.5 text-[10px] text-slate-500 max-h-[60px] overflow-y-auto">
            {savings.actions.slice(0, 4).map((a, i) => (
              <span key={i}>
                {a.date ? new Date(a.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : ""} — {a.count} excl. → {fmt(a.savings)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



/* ═══════ HISTORY SECTION ═══════ */
function HistorySection({ history, showHistory, setShowHistory, fmt }) {
  if (history.length === 0) return null;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <button onClick={() => setShowHistory(!showHistory)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/80 transition-colors" data-testid="toggle-history">
        <span className="font-semibold text-sm flex items-center gap-2">
          <History className="w-4 h-4 text-blue-400" />
          Historique des analyses ({history.length})
        </span>
        {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {showHistory && (
        <div className="px-4 pb-4 border-t border-slate-700/30 pt-3 space-y-2">
          {/* Waste evolution chart */}
          {history.length > 1 && (
            <div className="bg-slate-900/40 rounded-lg p-3 mb-3">
              <p className="text-xs text-slate-400 mb-2">Evolution du gaspillage</p>
              <div className="flex items-end gap-1 h-16">
                {[...history].reverse().map((h, i) => {
                  const maxW = Math.max(...history.map(x => x.waste_computed || 0), 1);
                  const height = Math.max(4, ((h.waste_computed || 0) / maxW) * 60);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full max-w-[24px] bg-red-400/40 hover:bg-red-400/70 rounded-t transition-all"
                        style={{ height: `${height}px` }}
                        title={`${fmt(h.waste_computed || 0)} — ${new Date(h.analyzed_at).toLocaleDateString("fr-FR")}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* History list */}
          <div className="overflow-y-auto max-h-[250px] space-y-1">
            {history.map((h, i) => {
              const vc = h.verdict_counts || {};
              return (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-900/30 rounded-lg text-xs" data-testid={`history-entry-${i}`}>
                  <div className="flex items-center gap-3">
                    <Clock className="w-3 h-3 text-slate-500" />
                    <span className="text-slate-300">{h.analyzed_at ? new Date(h.analyzed_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                    <span className="text-slate-500">{h.period_days}j</span>
                    {h.source === "weekly_cron" && <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-[10px]">Auto</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400">{h.terms_count} termes</span>
                    <span className="text-green-400">{vc.pertinent || 0}P</span>
                    <span className="text-amber-400">{vc.douteux || 0}D</span>
                    <span className="text-red-400">{vc.hors_sujet || 0}HS</span>
                    <span className="text-orange-400">{vc.hors_zone || 0}HZ</span>
                    <span className="text-red-300 font-medium tabular-nums">{fmt(h.waste_computed || 0)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════ WHITELIST SECTION ═══════ */
function WhitelistSection({ whitelist, showWhitelist, setShowWhitelist, removeFromWhitelist }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <button onClick={() => setShowWhitelist(!showWhitelist)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/80 transition-colors" data-testid="toggle-whitelist">
        <span className="font-semibold text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          Whitelist David ({whitelist.length})
          {whitelist.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">{whitelist.length} termes proteges</span>}
        </span>
        {showWhitelist ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {showWhitelist && (
        <div className="px-4 pb-4 border-t border-slate-700/30 pt-3">
          {whitelist.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Aucun terme whiteliste. Cliquez sur l'icone bouclier dans le tableau des termes pour en ajouter.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {whitelist.map((w, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/25 rounded-md text-xs text-emerald-300" data-testid={`whitelist-term-${i}`}>
                  <ShieldCheck className="w-3 h-3" />{w.term}
                  <button onClick={() => removeFromWhitelist(w.term)} className="hover:text-red-400 ml-1"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <p className="text-[10px] text-slate-500 mt-2">Les termes whitelistes seront toujours classes "pertinent" par David lors des prochaines analyses.</p>
        </div>
      )}
    </div>
  );
}


/* ═══════ ANALYSIS RESULTS ═══════ */
function AnalysisResults({ analysis, filter, setFilter, showRecos, setShowRecos, selectedForExclusion, toggleExclusion, selectAllNegatives, excludeSelected, excluding, fmt, addToWhitelist, whitelistedSet }) {
  const terms = analysis.terms || [];
  const counts = { pertinent: 0, douteux: 0, hors_sujet: 0, hors_zone: 0 };
  const costs = { pertinent: 0, douteux: 0, hors_sujet: 0, hors_zone: 0 };
  terms.forEach(t => {
    const v = t.verdict || "douteux";
    counts[v] = (counts[v] || 0) + 1;
    costs[v] = (costs[v] || 0) + (t.cost || 0);
  });

  const filtered = (filter === "all" ? terms : terms.filter(t => t.verdict === filter))
    .sort((a, b) => {
      // Sort douteux by score ascending (lowest score = most suspicious first)
      if (a.verdict === "douteux" && b.verdict === "douteux" && a.score != null && b.score != null) return a.score - b.score;
      return 0;
    });
  const wasteTotal = (costs.hors_sujet || 0) + (costs.hors_zone || 0);

  return (
    <>
      {/* Summary */}
      <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/30 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {analysis.analyzed_at ? new Date(analysis.analyzed_at).toLocaleString("fr-FR") : ""} — {terms.length} termes
          </p>
        </div>

        {analysis.summary && (
          <p className="text-sm text-slate-300 bg-slate-900/40 rounded-lg p-3 mb-4">{analysis.summary}</p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {Object.entries(VERDICT_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button key={key} onClick={() => setFilter(filter === key ? "all" : key)}
                className={`border rounded-lg p-3 text-left transition-all ${filter === key ? cfg.bg + " ring-1 ring-white/20" : "bg-slate-800/30 border-slate-700/30 hover:border-slate-600"}`}
                data-testid={`filter-${key}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className={`w-4 h-4 ${cfg.color}`} />
                  <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                </div>
                <p className="text-lg font-bold text-white">{counts[key] || 0}</p>
                <p className="text-[11px] text-slate-500">{fmt(costs[key] || 0)}</p>
              </button>
            );
          })}
        </div>

        {wasteTotal > 50 && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/25 rounded-lg flex items-center gap-3" data-testid="waste-alert">
            <DollarSign className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm font-semibold text-red-400">Gaspillage estime : {fmt(wasteTotal)} — {counts.hors_sujet + counts.hors_zone} termes</p>
          </div>
        )}
      </div>

      {/* Recommendations */}
      {analysis.recommendations?.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
          <button onClick={() => setShowRecos(!showRecos)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/80 transition-colors">
            <span className="font-semibold text-sm flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-400" />Recommandations ({analysis.recommendations.length})
            </span>
            {showRecos ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showRecos && (
            <div className="px-4 pb-3 space-y-1.5">
              {analysis.recommendations.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-slate-300 bg-slate-900/30 rounded-lg p-2.5">
                  <span className="text-amber-400 font-bold shrink-0">{i + 1}.</span><span>{r}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Suggested negatives from David */}
      {analysis.top_negatives?.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-red-400" />Negatifs suggeres par David ({analysis.top_negatives.length})
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {analysis.top_negatives.map((neg, i) => (
              <span key={i} className="px-2.5 py-1 bg-red-500/10 border border-red-500/25 rounded-md text-xs text-red-300 font-mono">-{neg}</span>
            ))}
          </div>
        </div>
      )}

      {/* Action bar for exclusions */}
      {selectedForExclusion.size > 0 && (
        <div className="bg-red-500/10 border border-red-500/25 rounded-xl p-3 flex items-center justify-between sticky bottom-2 backdrop-blur-sm" data-testid="exclusion-bar">
          <span className="text-sm text-red-300 font-medium">{selectedForExclusion.size} termes selectionnes</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedForExclusion(new Set())} className="px-3 py-1.5 text-xs text-slate-400 hover:text-white transition-colors">Deselectionner</button>
            <button onClick={excludeSelected} disabled={excluding}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-sm text-white font-medium flex items-center gap-2 transition-colors"
              data-testid="exclude-btn">
              <Ban className="w-4 h-4" />{excluding ? "Exclusion..." : "Exclure de Google Ads"}
            </button>
          </div>
        </div>
      )}

      {/* Terms table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <Search className="w-4 h-4 text-blue-400" />Termes ({filtered.length})
          </h4>
          <div className="flex items-center gap-2">
            {filter !== "all" && (
              <button onClick={() => setFilter("all")} className="text-xs text-blue-400 hover:underline">Voir tout</button>
            )}
            <button onClick={selectAllNegatives} className="text-xs text-red-400 hover:underline" data-testid="select-all-negatives">
              Selectionner hors sujet/zone
            </button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/50 sticky top-0">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-400">Terme</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-400">Campagne</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-400">Clics</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-400">Cout</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-400">Conv.</th>
                <th className="text-center px-3 py-2 text-xs font-medium text-slate-400">Verdict</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-400">Raison</th>
                <th className="w-8 px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/20">
              {filtered.map((t, i) => {
                const cfg = VERDICT_CONFIG[t.verdict] || VERDICT_CONFIG.douteux;
                const Icon = cfg.icon;
                const isSelected = selectedForExclusion.has(t.term);
                const isWhitelisted = whitelistedSet.has(t.term);
                const canWhitelist = t.verdict !== "pertinent" && !isWhitelisted;
                return (
                  <tr key={i} className={`transition-colors ${isSelected ? "bg-red-500/5" : isWhitelisted ? "bg-emerald-500/5" : "hover:bg-slate-700/10"}`}>
                    <td className="px-2 py-2 text-center">
                      <input type="checkbox" checked={isSelected} onChange={() => toggleExclusion(t.term)}
                        className="w-3.5 h-3.5 rounded accent-red-500 cursor-pointer" data-testid={`check-${i}`} />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs max-w-[200px] truncate" title={t.term}>
                      {t.term}
                      {isWhitelisted && <ShieldCheck className="w-3 h-3 text-emerald-400 inline ml-1" />}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-400 max-w-[140px] truncate" title={t.campaign}>{t.campaign}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{t.clicks}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-amber-400">{fmt(t.cost)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-purple-400">{t.conversions || 0}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${cfg.bg}`}>
                        <Icon className={`w-3 h-3 ${cfg.color}`} /><span className={cfg.color}>{cfg.label}</span>
                        {t.verdict === "douteux" && t.score != null && (
                          <span className={`ml-0.5 font-bold tabular-nums ${t.score <= 2 ? "text-red-400" : t.score >= 4 ? "text-green-400" : "text-amber-400"}`}>{t.score}/5</span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-400 max-w-[180px] truncate" title={t.raison}>{t.raison}</td>
                    <td className="px-2 py-2 text-center">
                      {canWhitelist && (
                        <button onClick={() => addToWhitelist(t.term)} className="p-1 hover:bg-emerald-500/20 rounded transition-colors" title="Whitelister ce terme"
                          data-testid={`whitelist-btn-${i}`}>
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400/60 hover:text-emerald-400" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}


/* ═══════ EXCLUSION MODAL ═══════ */

/* ═══════ SHARED LISTS SECTION (with campaign links) ═══════ */
function SharedListsSection({ sharedLists, campaignList, loadSharedLists }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listCampaigns, setListCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [linking, setLinking] = useState(false);
  const [campaignsToLink, setCampaignsToLink] = useState(new Set());

  const loadListCampaigns = async (listId) => {
    setLoadingCampaigns(true);
    setSelectedList(listId);
    setCampaignsToLink(new Set());
    try {
      const res = await authFetch(`${API}/google-ads/shared-list-campaigns/${listId}`);
      const data = await safeJson(res);
      setListCampaigns(data.campaigns || []);
    } catch {}
    setLoadingCampaigns(false);
  };

  const linkCampaigns = async () => {
    if (campaignsToLink.size === 0 || !selectedList) return;
    setLinking(true);
    try {
      const res = await authFetch(`${API}/google-ads/shared-list-campaigns/${selectedList}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_ids: [...campaignsToLink] }),
      });
      let data;
      data = await safeJson(res);
      if (data.success) {
        alert(`Liste rattachee a ${data.linked} campagne(s) !`);
        setCampaignsToLink(new Set());
        loadListCampaigns(selectedList);
        loadSharedLists();
      } else {
        alert("Erreur: " + (data.detail || "Echec"));
      }
    } catch (err) {
      alert("Erreur: " + err.message);
    }
    setLinking(false);
  };

  const linkedIds = new Set(listCampaigns.map(c => String(c.id)));
  const enabledCampaigns = campaignList.filter(c => c.status === "ENABLED");
  const unlinkedCampaigns = enabledCampaigns.filter(c => !linkedIds.has(String(c.id)));

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/80 transition-colors" data-testid="toggle-shared-lists">
        <span className="font-semibold text-sm flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-400" />
          Listes d'exclusion Google Ads ({sharedLists.length})
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-700/30 pt-3 space-y-2">
          {sharedLists.map(list => {
            const isSelected = selectedList === String(list.id);
            return (
              <div key={list.id} className={`rounded-lg border transition-all ${isSelected ? "border-blue-500/40 bg-blue-500/5" : "border-slate-700/30 bg-slate-900/20"}`}>
                <button onClick={() => isSelected ? setSelectedList(null) : loadListCampaigns(String(list.id))}
                  className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-slate-800/30 transition-colors rounded-lg"
                  data-testid={`shared-list-${list.id}`}>
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-blue-400/70" />
                    <span className="text-sm font-medium">{list.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{list.member_count} mots</span>
                  </div>
                  {isSelected ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <Eye className="w-3.5 h-3.5 text-slate-500" />}
                </button>

                {isSelected && (
                  <div className="px-3 pb-3 space-y-2">
                    {loadingCampaigns ? (
                      <div className="flex items-center gap-2 text-xs text-slate-400"><RefreshCw className="w-3 h-3 animate-spin" />Chargement...</div>
                    ) : (
                      <>
                        {/* Linked campaigns */}
                        <div>
                          <p className="text-[11px] text-slate-400 mb-1.5 font-medium">Campagnes rattachees ({listCampaigns.length})</p>
                          {listCampaigns.length === 0 ? (
                            <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2 py-1.5">
                              Aucune campagne ! Cette liste n'exclut rien.
                            </p>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {listCampaigns.map(c => (
                                <span key={c.id} className={`px-2 py-0.5 rounded text-[10px] border ${c.status === "ENABLED" ? "bg-green-500/10 border-green-500/20 text-green-400" : "bg-slate-700/30 border-slate-600/20 text-slate-500"}`}>
                                  {c.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Link to more campaigns */}
                        {unlinkedCampaigns.length > 0 && (
                          <div className="border-t border-slate-700/20 pt-2">
                            <p className="text-[11px] text-slate-400 mb-1.5 font-medium">Rattacher a d'autres campagnes actives</p>
                            <div className="flex flex-wrap gap-1 mb-2">
                              {unlinkedCampaigns.map(c => {
                                const checked = campaignsToLink.has(String(c.id));
                                return (
                                  <button key={c.id}
                                    onClick={() => { const next = new Set(campaignsToLink); checked ? next.delete(String(c.id)) : next.add(String(c.id)); setCampaignsToLink(next); }}
                                    className={`px-2 py-1 rounded text-[10px] border transition-all ${checked ? "bg-blue-500/20 border-blue-500/40 text-blue-300" : "bg-slate-700/20 border-slate-600/20 text-slate-400 hover:border-slate-500"}`}>
                                    {checked ? <CheckCircle className="w-3 h-3 inline mr-0.5" /> : <Plus className="w-3 h-3 inline mr-0.5" />}
                                    {c.name}
                                  </button>
                                );
                              })}
                            </div>
                            {campaignsToLink.size > 0 && (
                              <button onClick={linkCampaigns} disabled={linking}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-xs text-white font-medium flex items-center gap-1.5 transition-colors"
                                data-testid="link-campaigns-btn">
                                {linking ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                Rattacher a {campaignsToLink.size} campagne{campaignsToLink.size > 1 ? "s" : ""}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ExclusionModal({ count, target, setTarget, campaignList, campaignId, setCampaignId, sharedLists, listId, setListId, newListName, setNewListName, onConfirm, onCancel, excluding, accountLevelLists, selectedAccountListId, setSelectedAccountListId }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="exclusion-modal">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-lg w-full shadow-2xl">
        <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
          <Ban className="w-5 h-5 text-red-400" />
          Exclure {count} mot{count > 1 ? "s" : ""}-cle{count > 1 ? "s" : ""}
        </h3>
        <p className="text-sm text-slate-400 mb-5">Choisissez ou les ajouter comme mots-cles negatifs</p>

        <div className="space-y-3">
          {/* Option: Shared list */}
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${target === "shared_list" ? "border-blue-500/50 bg-blue-500/10" : "border-slate-700 hover:border-slate-600"}`}>
            <input type="radio" name="target" value="shared_list" checked={target === "shared_list"} onChange={() => setTarget("shared_list")} className="mt-1 accent-blue-500" />
            <div className="flex-1">
              <p className="font-medium text-sm">Liste d'exclusion existante</p>
              <p className="text-xs text-slate-400 mb-2">Ajouter a une liste partagee deja presente dans Google Ads</p>
              {target === "shared_list" && (
                <select value={listId} onChange={e => setListId(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-blue-500" data-testid="exclusion-list-select">
                  {sharedLists.map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.member_count} mots)</option>
                  ))}
                </select>
              )}
            </div>
          </label>

          {/* Option: New list */}
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${target === "new_list" ? "border-emerald-500/50 bg-emerald-500/10" : "border-slate-700 hover:border-slate-600"}`}>
            <input type="radio" name="target" value="new_list" checked={target === "new_list"} onChange={() => setTarget("new_list")} className="mt-1 accent-emerald-500" />
            <div className="flex-1">
              <p className="font-medium text-sm">Creer une nouvelle liste</p>
              <p className="text-xs text-slate-400 mb-2">Creer une liste d'exclusion et y ajouter les mots-cles</p>
              {target === "new_list" && (
                <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)}
                  placeholder="Nom de la nouvelle liste (ex: Exclusions David Mars 2026)"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-emerald-500" data-testid="new-list-name-input" />
              )}
            </div>
          </label>

          {/* Option: Campaign */}
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${target === "campaign" ? "border-amber-500/50 bg-amber-500/10" : "border-slate-700 hover:border-slate-600"}`}>
            <input type="radio" name="target" value="campaign" checked={target === "campaign"} onChange={() => setTarget("campaign")} className="mt-1 accent-amber-500" />
            <div className="flex-1">
              <p className="font-medium text-sm">Campagne specifique</p>
              <p className="text-xs text-slate-400 mb-2">Exclure uniquement sur une campagne donnee</p>
              {target === "campaign" && (
                <>
                  <select value={campaignId} onChange={e => setCampaignId(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-amber-500" data-testid="exclusion-campaign-select">
                    <option value="">-- Choisir une campagne --</option>
                    {campaignList.filter(c => c.status === "ENABLED").map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    <optgroup label="En pause">
                      {campaignList.filter(c => c.status !== "ENABLED").map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </optgroup>
                  </select>
                  {campaignId && campaignList.find(c => String(c.id) === String(campaignId) && c.name.toLowerCase().includes("max")) && (
                    <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-[11px] text-amber-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      <span>Les campagnes Performance Max ne supportent pas les negatifs au niveau campagne. Utilisez "Toutes les campagnes" ci-dessous.</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </label>

          {/* Option: Account-level (all campaigns including PMax) */}
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${target === "account" ? "border-purple-500/50 bg-purple-500/10" : "border-slate-700 hover:border-slate-600"}`}>
            <input type="radio" name="target" value="account" checked={target === "account"} onChange={() => setTarget("account")} className="mt-1 accent-purple-500" />
            <div className="flex-1">
              <p className="font-medium text-sm">Toutes les campagnes (niveau compte)</p>
              <p className="text-xs text-slate-400 mb-2">S'applique a TOUTES vos campagnes, y compris Performance Max</p>
              {target === "account" && accountLevelLists.length > 0 && (
                <div className="space-y-2">
                  <select value={selectedAccountListId} onChange={e => setSelectedAccountListId(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-purple-500" data-testid="account-list-select">
                    <option value="">Auto (liste avec le plus de place)</option>
                    {accountLevelLists.map(l => (
                      <option key={l.id} value={l.id}>{l.name} ({l.member_count}/1000)</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500">Si la liste est pleine, une nouvelle sera creee automatiquement</p>
                </div>
              )}
            </div>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors" data-testid="exclusion-cancel-btn">
            Annuler
          </button>
          <button onClick={onConfirm} disabled={excluding}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl text-sm text-white font-semibold flex items-center gap-2 transition-colors"
            data-testid="exclusion-confirm-btn">
            <Ban className="w-4 h-4" />
            {excluding ? "Exclusion en cours..." : `Exclure ${count} mot${count > 1 ? "s" : ""}-cle${count > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
